import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiCloudArrowUp, HiLink, HiXMark, HiSparkles } from 'react-icons/hi2';
import { toast } from 'react-hot-toast';
import {
  extractTextFromFile,
  fetchAndWrapFileFromUrl,
  extractGTMDetails,
} from '../../services/gtmExtractionService';

export default function GTMImportModal({ open, onClose, onImported }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [url, setUrl] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleImport = async () => {
    setProcessing(true);
    try {
      let file = selectedFile;
      if (!file && url) {
        try {
          file = await fetchAndWrapFileFromUrl(url);
        } catch (_) {
          toast.error(
            'Could not fetch file from the provided link. Please download the file and upload directly.'
          );
          setProcessing(false);
          return;
        }
      }
      if (!file) {
        toast.error('Please select a file or provide a link.');
        setProcessing(false);
        return;
      }
      const { text, meta } = await extractTextFromFile(file);
      if (!text || text.trim().length < 10) {
        toast.error('Unable to read content from the document.');
        setProcessing(false);
        return;
      }
      const details = extractGTMDetails(text);
      onImported?.({ rawText: text, meta, ...details });
      onClose?.();
      toast.success('GTM content extracted. Review and save.');
    } catch (err) {
      toast.error('Failed to import GTM document');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Import GTM from document</h3>
              <button onClick={onClose} className="rounded-md p-1 hover:bg-gray-100">
                <HiXMark className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="rounded-xl border-2 border-dashed border-gray-300 p-4 text-center">
                <HiCloudArrowUp className="mx-auto mb-2 h-10 w-10 text-blue-600" />
                <p className="text-sm text-gray-700">Upload a PDF, DOCX, image, or TXT</p>
                <input
                  type="file"
                  onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="gtm-import-input"
                />
                <label
                  htmlFor="gtm-import-input"
                  className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  <HiCloudArrowUp className="h-4 w-4" />
                  Choose File
                </label>
                {selectedFile && (
                  <p className="mt-2 truncate text-xs text-gray-600">{selectedFile.name}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Or import from a link
                </label>
                <div className="flex items-center gap-2">
                  <HiLink className="h-5 w-5 text-gray-400" />
                  <input
                    type="url"
                    placeholder="Paste a public link (Google Drive, Dropbox, etc.)"
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Ensure the link is publicly accessible or grants access.
                </p>
              </div>
              <button
                onClick={handleImport}
                disabled={processing || (!selectedFile && !url)}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {processing ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Extracting...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <HiSparkles className="h-4 w-4" />
                    Extract & Map to GTM
                  </span>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
